import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { ArrowRight, CalendarClock, Gamepad2 } from "lucide-react"
import { api } from "../../../../convex/_generated/api"
import { gameRegistry } from "@/games/registry"
import { useWildGuessSoloEnabled } from "@/lib/analytics/posthog"

export const Route = createFileRoute("/_authed/admin/games")({
  component: AdminGames,
})

function AdminGames() {
  const games = useQuery(api.games.listAll)
  const soloEnabled = useWildGuessSoloEnabled()
  const visibleGames =
    games?.filter((game) => soloEnabled || game.gameType !== "solo-trivia") ??
    []

  return (
    <div className="space-y-4">
      <div className="console-panel rounded-[1.35rem] p-5">
        <p className="console-kicker">Live ops</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight">All games</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-[color:var(--muted-foreground)]">
          Cross-host view for scheduled, live, and completed game runs.
        </p>
      </div>

      {games === undefined ? (
        <ListSkeleton rows={5} />
      ) : visibleGames.length === 0 ? (
        <div className="console-panel rounded-[1.35rem] border-dashed p-8 text-center text-sm text-[color:var(--muted-foreground)]">
          No games yet.
        </div>
      ) : (
        <ul className="console-panel divide-y divide-[color:var(--border)] overflow-hidden rounded-[1.35rem]">
          {visibleGames.map((game) => {
            const label =
              gameRegistry[game.gameType as keyof typeof gameRegistry]?.label ??
              game.gameType
            return (
              <li key={game._id}>
                <div className="flex flex-col gap-3 px-4 py-3.5 transition-colors hover:bg-[color:var(--surface-soft)] sm:flex-row sm:items-center sm:justify-between">
                  <Link
                    to="/host/games/$gameId"
                    params={{ gameId: game._id }}
                    className="group flex min-w-0 items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-soft)] text-[color:var(--primary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]">
                      <Gamepad2 className="h-5 w-5" aria-hidden />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold">{label}</span>
                      <span className="mt-1 flex items-center gap-1 truncate text-xs text-[color:var(--muted-foreground)]">
                        <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {new Date(game.startsAt ?? game.createdAt).toLocaleString()}
                      </span>
                    </span>
                  </Link>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <span className="rounded-full border border-[color:var(--border)] px-2 py-0.5 text-[11px] uppercase tracking-wide text-[color:var(--muted-foreground)]">
                      {game.status}
                    </span>
                    <Link
                      to="/host/games/$gameId"
                      params={{ gameId: game._id }}
                      search={{ view: "run" }}
                      className="inline-flex min-h-8 items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
                    >
                      Run game
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <ul className="console-panel divide-y divide-[color:var(--border)] rounded-[1.35rem]">
      {Array.from({ length: rows }).map((_, index) => (
        <li key={index} className="flex items-center gap-3 px-4 py-3.5">
          <div className="h-12 w-12 animate-pulse rounded-2xl bg-[color:var(--muted)]" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-44 max-w-full animate-pulse rounded bg-[color:var(--muted)]" />
            <div className="h-2.5 w-36 animate-pulse rounded bg-[color:var(--muted)]" />
          </div>
        </li>
      ))}
    </ul>
  )
}
